import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "src/config/apiBase";
import { decryptToken } from "../utils/getToken";
import { toast } from "react-toastify";
import {
    ArrowLeft,
    ChevronDown,
    ChevronUp,
    Eye,
    EyeOff,
    GripVertical,
    Plus,
    RotateCcw,
    Save,
    Settings2,
    Trash2,
    Zap,
} from "lucide-react";
import { Link } from "react-router-dom";
import "./AdmissionFormBuilder.css";
import "./AddAdmission.css";

const FIELD_TYPES = [
    { value: "text", label: "Text" },
    { value: "email", label: "Email" },
    { value: "tel", label: "Phone" },
    { value: "number", label: "Number" },
    { value: "date", label: "Date" },
    { value: "textarea", label: "Long text" },
    { value: "select", label: "Dropdown" },
    { value: "radio", label: "Radio buttons" },
    { value: "checkbox", label: "Checkbox" },
    { value: "file", label: "File upload" },
    { value: "aadhar", label: "Aadhaar number" },
];

const LOGIC_OPERATORS = [
    { value: "equals", label: "Equals" },
    { value: "not_equals", label: "Not equals" },
    { value: "contains", label: "Contains" },
    { value: "not_empty", label: "Is not empty" },
    { value: "empty", label: "Is empty" },
    { value: "gt", label: "Greater than" },
    { value: "lt", label: "Less than" },
];

const emptyLogicRule = () => ({ fieldId: "", operator: "equals", value: "" });

function FieldEditor({ field, index, allFields, sections, onChange, onRemove, onMoveUp, onMoveDown, canRemove }) {
    const [showLogic, setShowLogic] = useState(false);
    const otherFields = allFields.filter((f) => f.id !== field.id && f.type !== "section");

    const update = (patch) => onChange(index, { ...field, ...patch });

    const updateLogic = (key, rules) => {
        update({ logic: { ...(field.logic || {}), [key]: rules } });
    };

    const addOption = () => {
        const opts = [...(field.options || []), { value: `opt_${Date.now()}`, label: "New option" }];
        update({ options: opts });
    };

    return (
        <div className={`afb-field-card ${field.visible ? "" : "afb-field-card--hidden"}`}>
            <div className="afb-field-card__head">
                <GripVertical size={16} className="afb-field-card__grip" aria-hidden />
                <div className="afb-field-card__title">
                    <strong>{field.label || "Untitled field"}</strong>
                    {field.builtin && <span className="afb-badge afb-badge--builtin">Built-in</span>}
                    <span className="afb-badge">{field.type}</span>
                </div>
                <div className="afb-field-card__actions">
                    <button type="button" title="Move up" onClick={() => onMoveUp(index)} disabled={index === 0}>
                        <ChevronUp size={16} />
                    </button>
                    <button type="button" title="Move down" onClick={() => onMoveDown(index)} disabled={index === allFields.length - 1}>
                        <ChevronDown size={16} />
                    </button>
                    <button
                        type="button"
                        title={field.visible ? "Hide on form" : "Show on form"}
                        onClick={() => update({ visible: !field.visible })}
                    >
                        {field.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                    </button>
                    {canRemove && (
                        <button type="button" title="Remove field" className="afb-btn--danger" onClick={() => onRemove(index)}>
                            <Trash2 size={16} />
                        </button>
                    )}
                </div>
            </div>

            <div className="afb-field-card__body">
                <div className="afb-grid-2">
                    <label className="afb-label">
                        Label
                        <input
                            className="admission-form-input"
                            value={field.label}
                            onChange={(e) => update({ label: e.target.value })}
                        />
                    </label>
                    <label className="afb-label">
                        Section
                        <select
                            className="admission-form-select"
                            value={field.section}
                            onChange={(e) => update({ section: e.target.value })}
                        >
                            {sections.map((s) => (
                                <option key={s.id} value={s.id}>{s.label}</option>
                            ))}
                        </select>
                    </label>
                    {!field.builtin && (
                        <label className="afb-label">
                            Field type
                            <select
                                className="admission-form-select"
                                value={field.type}
                                onChange={(e) => update({ type: e.target.value })}
                            >
                                {FIELD_TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </label>
                    )}
                    <label className="afb-label">
                        Placeholder
                        <input
                            className="admission-form-input"
                            value={field.placeholder || ""}
                            onChange={(e) => update({ placeholder: e.target.value })}
                        />
                    </label>
                    <label className="afb-label afb-label--check">
                        <input
                            type="checkbox"
                            checked={Boolean(field.required)}
                            onChange={(e) => update({ required: e.target.checked })}
                        />
                        Required field
                    </label>
                </div>

                <label className="afb-label">
                    Help text
                    <input
                        className="admission-form-input"
                        value={field.helpText || ""}
                        onChange={(e) => update({ helpText: e.target.value })}
                        placeholder="Shown below the field on the public form"
                    />
                </label>

                {(field.type === "select" || field.type === "radio") && (
                    <div className="afb-options">
                        <strong>Options</strong>
                        {(field.options || []).map((opt, oi) => (
                            <div key={oi} className="afb-option-row">
                                <input
                                    className="admission-form-input"
                                    placeholder="Label"
                                    value={opt.label}
                                    onChange={(e) => {
                                        const opts = [...field.options];
                                        opts[oi] = { ...opt, label: e.target.value, value: e.target.value };
                                        update({ options: opts });
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => update({ options: field.options.filter((_, i) => i !== oi) })}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                        <button type="button" className="afb-btn-secondary" onClick={addOption}>
                            <Plus size={14} /> Add option
                        </button>
                    </div>
                )}

                <button
                    type="button"
                    className="afb-logic-toggle"
                    onClick={() => setShowLogic((v) => !v)}
                >
                    <Zap size={14} />
                    Conditional logic
                    {showLogic ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>

                {showLogic && (
                    <div className="afb-logic-panel">
                        <p className="afb-hint">Show this field only when all conditions below are met.</p>
                        {(field.logic?.showWhen || []).map((rule, ri) => (
                            <div key={ri} className="afb-logic-row">
                                <select
                                    className="admission-form-select"
                                    value={rule.fieldId}
                                    onChange={(e) => {
                                        const rules = [...(field.logic?.showWhen || [])];
                                        rules[ri] = { ...rule, fieldId: e.target.value };
                                        updateLogic("showWhen", rules);
                                    }}
                                >
                                    <option value="">Select field…</option>
                                    {otherFields.map((f) => (
                                        <option key={f.id} value={f.id}>{f.label}</option>
                                    ))}
                                </select>
                                <select
                                    className="admission-form-select"
                                    value={rule.operator}
                                    onChange={(e) => {
                                        const rules = [...(field.logic?.showWhen || [])];
                                        rules[ri] = { ...rule, operator: e.target.value };
                                        updateLogic("showWhen", rules);
                                    }}
                                >
                                    {LOGIC_OPERATORS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                                {!["not_empty", "empty"].includes(rule.operator) && (
                                    <input
                                        className="admission-form-input"
                                        placeholder="Value"
                                        value={rule.value}
                                        onChange={(e) => {
                                            const rules = [...(field.logic?.showWhen || [])];
                                            rules[ri] = { ...rule, value: e.target.value };
                                            updateLogic("showWhen", rules);
                                        }}
                                    />
                                )}
                                <button
                                    type="button"
                                    onClick={() => updateLogic("showWhen", (field.logic?.showWhen || []).filter((_, i) => i !== ri))}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            className="afb-btn-secondary"
                            onClick={() => updateLogic("showWhen", [...(field.logic?.showWhen || []), emptyLogicRule()])}
                        >
                            <Plus size={14} /> Add show condition
                        </button>

                        <p className="afb-hint afb-hint--spaced">Make required when all conditions below are met.</p>
                        {(field.logic?.requiredWhen || []).map((rule, ri) => (
                            <div key={ri} className="afb-logic-row">
                                <select
                                    className="admission-form-select"
                                    value={rule.fieldId}
                                    onChange={(e) => {
                                        const rules = [...(field.logic?.requiredWhen || [])];
                                        rules[ri] = { ...rule, fieldId: e.target.value };
                                        updateLogic("requiredWhen", rules);
                                    }}
                                >
                                    <option value="">Select field…</option>
                                    {otherFields.map((f) => (
                                        <option key={f.id} value={f.id}>{f.label}</option>
                                    ))}
                                </select>
                                <select
                                    className="admission-form-select"
                                    value={rule.operator}
                                    onChange={(e) => {
                                        const rules = [...(field.logic?.requiredWhen || [])];
                                        rules[ri] = { ...rule, operator: e.target.value };
                                        updateLogic("requiredWhen", rules);
                                    }}
                                >
                                    {LOGIC_OPERATORS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                                {!["not_empty", "empty"].includes(rule.operator) && (
                                    <input
                                        className="admission-form-input"
                                        placeholder="Value"
                                        value={rule.value}
                                        onChange={(e) => {
                                            const rules = [...(field.logic?.requiredWhen || [])];
                                            rules[ri] = { ...rule, value: e.target.value };
                                            updateLogic("requiredWhen", rules);
                                        }}
                                    />
                                )}
                                <button
                                    type="button"
                                    onClick={() => updateLogic("requiredWhen", (field.logic?.requiredWhen || []).filter((_, i) => i !== ri))}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            className="afb-btn-secondary"
                            onClick={() => updateLogic("requiredWhen", [...(field.logic?.requiredWhen || []), emptyLogicRule()])}
                        >
                            <Plus size={14} /> Add required condition
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

const AdmissionFormBuilder = () => {
    const url = API_BASE_URL;
    const token = decryptToken("accessToken") || "";
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("fields");

    const authHeaders = useMemo(
        () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
        [token],
    );

    const loadConfig = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${url}/admission/form/config`, { headers: authHeaders });
            if (!res.ok) throw new Error("Failed to load");
            const data = await res.json();
            setConfig(data.config);
        } catch {
            toast.error("Could not load form configuration");
        } finally {
            setLoading(false);
        }
    }, [url, authHeaders]);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    const updateSettings = (patch) => {
        setConfig((prev) => ({ ...prev, settings: { ...prev.settings, ...patch } }));
    };

    const updateField = (index, field) => {
        setConfig((prev) => {
            const fields = [...prev.fields];
            fields[index] = field;
            return { ...prev, fields };
        });
    };

    const addCustomField = () => {
        const id = `custom_${Date.now()}`;
        setConfig((prev) => ({
            ...prev,
            fields: [
                ...prev.fields,
                {
                    id,
                    fieldKey: id,
                    label: "New custom field",
                    type: "text",
                    section: prev.sections?.[0]?.id || "student",
                    builtin: false,
                    visible: true,
                    required: false,
                    order: prev.fields.length,
                    placeholder: "",
                    helpText: "",
                    options: [],
                    validation: {},
                    logic: { showWhen: [], requiredWhen: [] },
                },
            ],
        }));
    };

    const removeField = (index) => {
        setConfig((prev) => ({
            ...prev,
            fields: prev.fields.filter((_, i) => i !== index),
        }));
    };

    const moveField = (index, direction) => {
        setConfig((prev) => {
            const fields = [...prev.fields];
            const target = index + direction;
            if (target < 0 || target >= fields.length) return prev;
            [fields[index], fields[target]] = [fields[target], fields[index]];
            return {
                ...prev,
                fields: fields.map((f, i) => ({ ...f, order: i })),
            };
        });
    };

    const save = async () => {
        setSaving(true);
        try {
            const res = await fetch(`${url}/admission/form/config`, {
                method: "PUT",
                headers: authHeaders,
                body: JSON.stringify({ config }),
            });
            if (!res.ok) throw new Error("Save failed");
            const data = await res.json();
            setConfig(data.config);
            toast.success("Form configuration saved");
        } catch {
            toast.error("Could not save form configuration");
        } finally {
            setSaving(false);
        }
    };

    const resetDefaults = async () => {
        if (!window.confirm("Reset form to default fields and settings? This cannot be undone.")) return;
        try {
            const res = await fetch(`${url}/admission/form/config/reset`, {
                method: "POST",
                headers: authHeaders,
            });
            if (!res.ok) throw new Error("Reset failed");
            const data = await res.json();
            setConfig(data.config);
            toast.success("Form reset to defaults");
        } catch {
            toast.error("Could not reset form");
        }
    };

    if (loading || !config) {
        return (
            <div className="admission-form-shell">
                <div className="admission-form-container">
                    <p className="afb-loading">Loading form builder…</p>
                </div>
            </div>
        );
    }

    const visibleCount = config.fields.filter((f) => f.visible).length;

    return (
        <div className="admission-form-shell afb-shell">
            <div className="admission-form-container afb-container">
                <div className="admission-form-header">
                    <Link to="/admission" className="admission-form-back">
                        <ArrowLeft size={18} /> Back to admissions
                    </Link>
                    <div className="admission-form-header-text">
                        <h1>Admission Form Builder</h1>
                        <p>Customize which fields appear on your public admission form. Changes apply instantly on your website.</p>
                    </div>
                    <div className="afb-header-actions">
                        <button type="button" className="admission-form-btn admission-form-btn--secondary" onClick={resetDefaults}>
                            <RotateCcw size={16} /> Reset
                        </button>
                        <button type="button" className="admission-form-btn admission-form-btn--primary" onClick={save} disabled={saving}>
                            <Save size={16} /> {saving ? "Saving…" : "Save changes"}
                        </button>
                    </div>
                </div>

                <div className="afb-stats">
                    <div className="afb-stat">
                        <strong>{visibleCount}</strong>
                        <span>Visible fields</span>
                    </div>
                    <div className="afb-stat">
                        <strong>{config.fields.filter((f) => !f.builtin).length}</strong>
                        <span>Custom fields</span>
                    </div>
                    <div className="afb-stat">
                        <strong>{config.settings.enabled !== false ? "Open" : "Closed"}</strong>
                        <span>Form status</span>
                    </div>
                </div>

                <div className="afb-tabs">
                    <button type="button" className={tab === "fields" ? "afb-tab--active" : ""} onClick={() => setTab("fields")}>
                        Fields ({config.fields.length})
                    </button>
                    <button type="button" className={tab === "settings" ? "afb-tab--active" : ""} onClick={() => setTab("settings")}>
                        <Settings2 size={14} /> Form settings
                    </button>
                </div>

                {tab === "settings" && (
                    <div className="admission-form-card afb-settings-card">
                        <div className="afb-grid-2">
                            <label className="afb-label">
                                Form title
                                <input
                                    className="admission-form-input"
                                    value={config.settings.title || ""}
                                    onChange={(e) => updateSettings({ title: e.target.value })}
                                />
                            </label>
                            <label className="afb-label">
                                Primary color
                                <input
                                    type="color"
                                    className="afb-color-input"
                                    value={config.settings.primaryColor || "#6366f1"}
                                    onChange={(e) => updateSettings({ primaryColor: e.target.value })}
                                />
                            </label>
                        </div>
                        <label className="afb-label">
                            Subtitle
                            <textarea
                                className="admission-form-input"
                                rows={2}
                                value={config.settings.subtitle || ""}
                                onChange={(e) => updateSettings({ subtitle: e.target.value })}
                            />
                        </label>
                        <label className="afb-label">
                            Terms text
                            <textarea
                                className="admission-form-input"
                                rows={2}
                                value={config.settings.termsText || ""}
                                onChange={(e) => updateSettings({ termsText: e.target.value })}
                            />
                        </label>
                        <div className="afb-grid-2">
                            <label className="afb-label">
                                Success title
                                <input
                                    className="admission-form-input"
                                    value={config.settings.successTitle || ""}
                                    onChange={(e) => updateSettings({ successTitle: e.target.value })}
                                />
                            </label>
                            <label className="afb-label">
                                Success message
                                <input
                                    className="admission-form-input"
                                    value={config.settings.successMessage || ""}
                                    onChange={(e) => updateSettings({ successMessage: e.target.value })}
                                />
                            </label>
                        </div>
                        <div className="afb-settings-toggles">
                            <label className="afb-label afb-label--check">
                                <input
                                    type="checkbox"
                                    checked={config.settings.enabled !== false}
                                    onChange={(e) => updateSettings({ enabled: e.target.checked })}
                                />
                                Accept new applications
                            </label>
                            <label className="afb-label afb-label--check">
                                <input
                                    type="checkbox"
                                    checked={Boolean(config.settings.showPlanSelection)}
                                    onChange={(e) => updateSettings({ showPlanSelection: e.target.checked })}
                                />
                                Show plan selection step
                            </label>
                            <label className="afb-label afb-label--check">
                                <input
                                    type="checkbox"
                                    checked={config.settings.multiStep !== false}
                                    onChange={(e) => updateSettings({ multiStep: e.target.checked })}
                                />
                                Multi-step form (better UX on mobile)
                            </label>
                        </div>
                    </div>
                )}

                {tab === "fields" && (
                    <>
                        <div className="afb-fields-toolbar">
                            <button type="button" className="admission-form-btn admission-form-btn--primary" onClick={addCustomField}>
                                <Plus size={16} /> Add custom field
                            </button>
                            <Link to="/admission-plans" className="admission-form-btn admission-form-btn--secondary">
                                Manage admission plans →
                            </Link>
                        </div>

                        <div className="afb-fields-list">
                            {config.fields.map((field, index) => (
                                <FieldEditor
                                    key={field.id}
                                    field={field}
                                    index={index}
                                    allFields={config.fields}
                                    sections={config.sections}
                                    onChange={updateField}
                                    onRemove={removeField}
                                    onMoveUp={(i) => moveField(i, -1)}
                                    onMoveDown={(i) => moveField(i, 1)}
                                    canRemove={!field.builtin}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AdmissionFormBuilder;
